import { ChangeDetectorRef, Component, Inject, Input } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Header, User } from "@aptsi-types";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { TimeFormatService } from "src/app/features/ada-access/shared/service/time-format.service";
import { UserService } from "../user.service";
import { DeleteDialogComponent } from "../../shared/dialogs/delete-dialog/delete-dialog.component";
import { KeycloakStateService } from 'src/app/core/service/utility.service';

@Component({
  selector: "app-user-search",
  templateUrl: "./user-search.component.html",
  styleUrls: ["./user-search.component.scss"],
})
export class UserSearchComponent {
  isCreateMode = false;
  isUpdateMode = false;
  selectedUserId: string | null = null;

  // ─── Pagination ───────────────────────────────────────────────
  currentPage = 1;
  itemsPerPage = 7;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;

  // ─── Search & Filtering ──────────────────────────────────────
  searchText: string = null;
  filterByHeader: string = null;
  isSearch = false;
  searchTextChanged = new Subject<string>();

  // ─── Data ─────────────────────────────────────────────────────
  user: User[] = [];
  filteredData: User[] = [];
  searchedData: User[] = [];
  organizationId: string;

  // ─── UI Configuration ─────────────────────────────────────────
  headers: Header[] = [
    {
      name: "User Id",
      property: "userId",
      showInDropdown: true,
      description: "Unique identifier for the user",
    },
    {
      name: "First Name",
      property: "firstName",
      showInDropdown: true,
      description: "First name of the user",
    },

    {
      name: "Last Name",
      property: "lastName",
      showInDropdown: true,
      description: "Last name of the user",
    },

    {
      name: "Email Address",
      property: "email",
      showInDropdown: true,
      description: "Email address of the user",
    },

    {
      name: "Role",
      property: "roleName",
      showInDropdown: true,
      description: "Role of the user",
    },
  ];

  buttonConfig = {
    update: true,
    delete: true,
    capture: false,
  };

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private timeFormatService: TimeFormatService,
    private userService: UserService,
    private keycloakStateService: KeycloakStateService,
    @Inject(MAT_DIALOG_DATA)
  public data: any
  ) {}

  // ──────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────
  async ngOnInit(): Promise<void> {
       const dropdownHeaders = this.headers.filter(
      header => header.showInDropdown
    );

    if (dropdownHeaders.length > 1) {
      this.filterByHeader = dropdownHeaders[1].property;
    }

     this.organizationId =
    this.keycloakStateService.getOrganizationId();

  if (
    this.data?.initialUsers?.length
  ) {

    this.formatAndSetUsers(
      this.data.initialUsers,
      false
    );

  } else {

    await this.fetchUsers();
  }


    this.searchTextChanged
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((text) => {
        this.searchText = text;
        this.searchUsers();
      });

    this.cdr.detectChanges();
  }

  // ──────────────────────────────────────────────────────────────
  // Getters
  // ──────────────────────────────────────────────────────────────
  get paginatedData(): User[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.isSearch
      ? this.searchedData.slice(start, end)
      : this.filteredData.slice(start, end);
  }

  // ──────────────────────────────────────────────────────────────
  // Event Handlers
  // ──────────────────────────────────────────────────────────────
  changePage(page: number): void {
    this.currentPage = page;
    this.totalPages = this.isSearch
      ? this.totalPagesSearch
      : this.totalPagesNormal;
    this.isSearch ? this.fetchUserSearch() : this.fetchUsers();
  }

  onSearchTextChange(searchValue: string): void {
    this.searchTextChanged.next(searchValue);
  }

  handleAction(event: {
    action: string;
    rowIndex: number;
    rowData: User;
  }): void {
    if (event.action === "update") {
      this.updateUser(event.rowIndex);
    } else if (event.action === "delete") {
      this.deleteUser(event.rowIndex);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Navigation
  // ──────────────────────────────────────────────────────────────

  createUser(): void {
    this.isCreateMode = true;
    this.selectedUserId = null;
  }

  updateUser(index: number): void {
    this.isUpdateMode = true;
    this.selectedUserId = this.isSearch
      ? this.searchedData[index].userId
      : this.filteredData[index].userId;
  }

  closeForm(): void {
    this.isCreateMode = false;
    this.isUpdateMode = false;
    this.selectedUserId = null;
  }

  onUserSaved(): void {
    console.log("✅ onUserSaved() triggered in parent");

    setTimeout(() => {
      this.fetchUsers(); // fetch after 1 second
      this.closeForm(); // close form after fetch
    }, 1000); // 1000 ms = 1 second
  }

  back(): void {
    if (this.isSearch) {
      this.isSearch = false;
      this.fetchUsers();
    } else {
      this.router.navigate(["ada-access/auth-tools/policy-authoring"]);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Data Fetching
  // ──────────────────────────────────────────────────────────────
  async fetchUsers(): Promise<void> {
    try {
      const response = await this.userService
        .getUsers(this.currentPage - 1,this.organizationId)
        .toPromise();
      this.formatAndSetUsers(response, false);
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  }

  async fetchUserSearch(): Promise<void> {
    try {
      const response = await this.userService
        .getUserSearch(
          this.currentPage - 1,
          this.searchText,
          this.filterByHeader,
          this.organizationId
        )
        .toPromise();
      this.formatAndSetUsers(response, true);
    } catch (error) {
      console.error("Error fetching user search:", error);
    }
  }

  private formatAndSetUsers(response: any[], isSearch: boolean): void {
    const formatted = response.map((user) => ({
      ...user,
      timestamp: this.timeFormatService.formatDate(user.timestamp),
    }));

    if (isSearch) {
      this.searchedData = formatted;
      this.totalPagesSearch = response[0]?.totalPages || 0;
    } else {
      this.filteredData = formatted;
      this.totalPagesNormal = response[0]?.totalPages || 0;
    }

    this.updatePagination(response);
  }

  private updatePagination(response: any[]): void {
    if (response.length > 0 && response[0].totalPages) {
      const totalPages = response[0].totalPages;
      const currentPage = response[0].currentPage + 1;

      if (this.isSearch) {
        this.totalPagesSearch = totalPages;
      } else {
        this.totalPagesNormal = totalPages;
      }

      this.totalPages = totalPages;
      this.currentPage = currentPage;
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Search
  // ──────────────────────────────────────────────────────────────
  // async searchRules(): Promise<void> {
  //   try {
  //     if (this.searchText) {
  //       this.isSearch = true;

  //       await this.fetchUserSearch();
  //       this.totalPages = this.totalPagesSearch;

  //       const selectedHeader = this.headers.find(
  //         (h) => h.property === this.filterByHeader && h.showInDropdown
  //       );
  //       if (selectedHeader) {
  //         this.searchedData = this.searchedData.filter((rule) => {
  //           const value = rule[selectedHeader.property];
  //           return value
  //             ?.toString()
  //             .toLowerCase()
  //             .includes(this.searchText.toLowerCase());
  //         });

  //         this.searchedData.sort((a, b) =>
  //           a[selectedHeader.property]
  //             ?.toString()
  //             .localeCompare(b[selectedHeader.property]?.toString())
  //         );
  //       } else {
  //         this.filteredData = [];
  //       }
  //     } else {
  //       this.isSearch = false;

  //       await this.fetchUsers();
  //     }
  //   } catch (error) {
  //     console.error("Error during search:", error);
  //   }
  // }


  async searchUsers(): Promise<void> {

    try {

      if (this.searchText?.trim()) {

        this.isSearch = true;
        this.currentPage = 1;

        if (!this.filterByHeader) {
          this.filterByHeader = 'all';
        }

        // Backend handles:
        // search
        // pagination
        // filtering
        await this.fetchUserSearch();

      } else {

        this.isSearch = false;
        this.currentPage = 1;

        await this.fetchUsers();
      }

    } catch (error) {

      console.error(
        'Error during search:',
        error
      );
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Delete
  // ──────────────────────────────────────────────────────────────
  async deleteUser(index: number): Promise<void> {
    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Are you sure?",
          message: "Do you really want to delete this User?",
          isConfirm: false,
        },
      });

      const result = await dialogRef.afterClosed().toPromise();
      if (!result) return;

      const userId = this.isSearch
        ? this.searchedData[index].userId
        : this.filteredData[index].userId;

      const username = this.isSearch
        ? this.searchedData[index].username
        : this.filteredData[index].username;

      await this.userService.deleteUser(userId, username).toPromise();

      this.isSearch ? this.fetchUserSearch() : this.fetchUsers();

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Success",
          message: "The User has been deleted successfully.",
          isConfirm: true,
        },
      });
    } catch (error) {
      console.error("Error deleting User:", error);
    }
  }
}
